import { apiHosts } from "@/api/constants/apiHosts";
import type { ReportPayment, ReportPaymentNew } from "@/views/reportPayment/reportPayment";
import { NotifyOptionsError, NotifyOptionsSuccess } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";

import reportPaymentService from "./reportPaymentService";
import { getDistributorReportById } from "../distributorReport/distributorReportApi";

const URL = "report-payments";
const ENTITY_NAME = "Payment";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_ODR_API_BASE_URL,
};

const api = apiService<ReportPayment, Paging<ReportPayment>>(apiHosts.ODR, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getReportPayments = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getReportPaymentById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewReportPayment = async (complianceEventId?: string | number) => {
  if (complianceEventId) {
    return Promise.all([getDistributorReportById(complianceEventId)]).then(([result]) => {
      //TODO:TS This was previously hiding all notifs for this call. revisit
      const data = reportPaymentService.getInitialData();
      data.report = result.data;
      data.reportId = result.data.id;

      return { data } as AxiosResponse<ReportPaymentNew, any>;
    });
  } else {
    const data = reportPaymentService.getInitialData() as ReportPaymentNew;
    return { data } as AxiosResponse<ReportPaymentNew, any>;
  }
};

const createReportPayment = (reportPayment: ReportPayment, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, reportPayment, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateReportPayment = (reportPayment: ReportPayment, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + reportPayment.id, reportPayment, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {ReportPayment} reportPayment This cannot be a 'ref' to a reportPayment
 */
const saveReportPayment = (reportPayment: ReportPayment, config: PagingApiAndNotifierConfig = {}) => {
  if (reportPayment.id) {
    return updateReportPayment(reportPayment, config);
  } else {
    return createReportPayment(reportPayment, config);
  }
};

const deleteReportPaymentById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  createReportPayment,
  deleteReportPaymentById,
  getReportPaymentById,
  getReportPayments,
  getNewReportPayment,
  saveReportPayment,
  updateReportPayment,
};
